import { projectBodySchema, type Course, type Project } from '@rcs/data-schema';
import { createSampleCourse } from '../course/sample';

/** サンプルプロジェクトの名前。 */
export const SAMPLE_PROJECT_NAME = 'はじめてのコースター';

/** サンプルプロジェクトの ID。 */
const SAMPLE_PROJECT_ID = '00000000-0000-4000-8000-0000000000a1';

/** サンプルのコースの見本（仮のコースの名前・表示名・ID・既定の車両）。 */
export interface SampleCatalogEntry {
  /** 仮のコース（`SAMPLE_COURSES`）の名前 */
  readonly sample: string;
  /** 表示名 */
  readonly name: string;
  /** サンプルプロジェクトでのコースの ID */
  readonly id: string;
  /** 既定の車両（省略は標準のコースター） */
  readonly vehicle?: string;
}

/** 超高速の発進のコース向けの車両。 */
const HIGH_SPEED_LAUNCH = 'builtin:vehicle/high-speed-launch';

/**
 * サンプルのコースの一覧（サンプルプロジェクトの全コース。「サンプルから追加」の選択肢にもする）。
 * 実在のコースターを真似たものは「〜風」と付ける（公開されている概要からの近似で、実物の図面ではない）。
 */
export const SAMPLE_CATALOG: readonly SampleCatalogEntry[] = [
  { sample: 'default', name: 'キャメルバック', id: '00000000-0000-4000-8000-000000000001' },
  { sample: 'launch', name: 'ローンチ', id: '00000000-0000-4000-8000-000000000002' },
  { sample: 'loop', name: 'ループ', id: '00000000-0000-4000-8000-000000000003' },
  { sample: 'helix', name: 'ヘリックス', id: '00000000-0000-4000-8000-000000000004' },
  {
    sample: 'highland',
    name: 'ハイランド鉄道（蒸気機関車）',
    id: '00000000-0000-4000-8000-000000000005',
    vehicle: 'builtin:vehicle/steam-locomotive',
  },
  {
    sample: 'high-speed',
    name: '高速新線（新幹線）',
    id: '00000000-0000-4000-8000-000000000006',
    vehicle: 'builtin:vehicle/bullet-train',
  },
  {
    sample: 'downhill',
    name: 'ダウンヒル（自転車）',
    id: '00000000-0000-4000-8000-000000000007',
    vehicle: 'builtin:vehicle/bicycle',
  },
  {
    sample: 'dodonpa',
    name: 'ドドンパ風',
    id: '00000000-0000-4000-8000-000000000008',
    vehicle: HIGH_SPEED_LAUNCH,
  },
  {
    sample: 'do-dodonpa',
    name: 'ド・ドドンパ風',
    id: '00000000-0000-4000-8000-000000000009',
    vehicle: HIGH_SPEED_LAUNCH,
  },
  { sample: 'fujiyama', name: 'FUJIYAMA 風', id: '00000000-0000-4000-8000-00000000000a' },
  { sample: 'eejanaika', name: 'ええじゃないか風', id: '00000000-0000-4000-8000-00000000000b' },
  { sample: 'takabisha', name: '高飛車風', id: '00000000-0000-4000-8000-00000000000c' },
  {
    sample: 'formula-rossa',
    name: 'フォーミュラ・ロッサ風',
    id: '00000000-0000-4000-8000-00000000000d',
    vehicle: HIGH_SPEED_LAUNCH,
  },
];

/**
 * 見本から、保存データの形式のコースを作る（既定の車両・環境も付ける）。
 *
 * @param id コースの ID（既定：見本の ID。プロジェクトに追加するときは新しい ID を渡す）
 * @param now 作成日時（ISO 8601）
 */
export function createCatalogCourse(
  entry: SampleCatalogEntry,
  now: string,
  id: string = entry.id,
): Course {
  return {
    ...createSampleCourse(entry.sample, { id, name: entry.name, now }),
    defaultVehicleId: entry.vehicle ?? 'builtin:vehicle/standard-steel',
    defaultEnvironmentId: 'builtin:environment/test-space',
  };
}

/**
 * 初回起動で作るサンプルプロジェクト（FR-PRJ-001, 002、B-55）。駅を出発して最後の停止ブレーキで
 * 止まるコースを 13 本持つ（{@link SAMPLE_CATALOG}。車両は、コースごとの既定を選んでいる。画面で変えられる）：
 *
 * - キャメルバック：チェーンリフト → 落下 → キャメルバック 2 つ
 * - ローンチ：発進加速（0 → 108 km/h）→ トップハット → キャメルバック
 * - ループ：チェーンリフト → 落下 → 垂直ループ
 * - ヘリックス：チェーンリフト → 落下 → バンク付きヘリックス
 * - ハイランド鉄道：蒸気機関車で、ゆるやかに加速 → 起伏 → カーブ → 停車
 * - 高速新線：新幹線で、216 km/h まで加速 → 巡航 → カーブ → 停車
 * - ダウンヒル：自転車で、峠から S 字カーブの下り坂を下る
 * - 実在のコースターを真似たもの：ドドンパ風・ド・ドドンパ風・FUJIYAMA 風・ええじゃないか風・高飛車風・
 *   フォーミュラ・ロッサ風（`course/famous.ts`）
 *
 * ユーザー定義の車両・環境は持たない（組み込みを使う）。
 *
 * @param now 作成日時（ISO 8601）
 */
export function createSampleProject(now: string): Project {
  const courses = SAMPLE_CATALOG.map((entry) => createCatalogCourse(entry, now));
  return projectBodySchema.parse({
    id: SAMPLE_PROJECT_ID,
    name: SAMPLE_PROJECT_NAME,
    createdAt: now,
    updatedAt: now,
    courses,
    vehicles: [],
    environments: [],
  });
}
